import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Globe2 } from 'lucide-react';
import { INTL_SHOPPING_PRICING } from '../../data/mock';
import { img } from '../../lib/img';

// Four headline service families per the conversion-focused homepage brief.
const SERVICE_BLOCKS = [
  {
    id: 'personal-styling',
    eyebrow: 'Personal Styling',
    title: 'Build a wardrobe that reflects who you are and where you\u2019re headed.',
    body: 'A founder-led image strategy session that decodes your body, color, lifestyle and ambition into a clear personal style direction.',
    image: 'https://customer-assets.emergentagent.com/job_mondaine-luxury/artifacts/r989wwnh_ChatGPT%20Image%20Jun%2012%2C%202026%2C%2011_05_37%20PM.jpeg',
    to: '/personal-styling',
  },
  {
    id: 'wardrobe-audit',
    eyebrow: 'Wardrobe Audit',
    title: 'Keep what works. Remove what doesn\u2019t. Create clarity.',
    body: 'An in-home edit that retires what no longer serves you and reorganises what does \u2014 leaving you with outfits, not options.',
    image: 'https://customer-assets.emergentagent.com/job_mondaine-luxury/artifacts/6iyh99wq_ChatGPT%20Image%20Jun%2012%2C%202026%2C%2011_16_02%20PM.png',
    to: '/personal-styling#wardrobe-edit',
  },
  {
    id: 'personal-shopping',
    eyebrow: 'Personal Shopping',
    title: 'Shop smarter with expert guidance.',
    body: 'A stylist-led shopping day across the right brands and stores \u2014 every piece chosen to extend your wardrobe with intent.',
    image: 'https://customer-assets.emergentagent.com/job_mondaine-luxury/artifacts/7di7e03w_ChatGPT%20Image%20Jun%2012%2C%202026%2C%2011_14_15%20PM.png',
    to: '/personal-styling#personal-shopping',
  },
  {
    id: 'corporate-workshops',
    eyebrow: 'Corporate Workshops',
    title: 'Professional image and executive presence training for teams.',
    body: 'Workshops for leadership teams, retail brands and graduating cohorts \u2014 from executive dressing to first-impression psychology.',
    image: 'https://customer-assets.emergentagent.com/job_mondaine-luxury/artifacts/vrh37ult_ChatGPT%20Image%20Jun%208%2C%202026%2C%2010_41_40%20PM.png',
    to: '/corporate-training',
  },
];

export default function SignatureServices() {
  return (
    <section className="py-24 lg:py-36 bg-ivory">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16 reveal">
          <div className="max-w-2xl">
            <p className="text-[11px] tracking-[0.3em] uppercase text-champagne-deep mb-6">Services</p>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] text-charcoal">
              Image strategy, end <em className="italic-accent">to end.</em>
            </h2>
          </div>
          <p className="max-w-md text-charcoal/70 leading-relaxed">
            From a one-room conversation to a full international shopping experience — every engagement is private, founder-led and intentionally limited.
          </p>
        </div>

        {/* Four service blocks */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-charcoal/10 reveal">
          {SERVICE_BLOCKS.map((s, idx) => (
            <Link
              key={s.id}
              to={s.to}
              data-testid={`service-block-${s.id}`}
              className="group bg-ivory p-6 lg:p-8 flex flex-col"
              style={{ transitionDelay: `${idx * 40}ms` }}
            >
              <div className="relative aspect-[4/5] overflow-hidden mb-6">
                <img loading="lazy" decoding="async" src={img(s.image, 900)} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
              </div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-champagne-deep">{s.eyebrow}</p>
              <h3 className="font-serif text-xl lg:text-2xl mt-3 text-charcoal leading-tight">{s.title}</h3>
              <p className="mt-4 text-sm text-charcoal/70 leading-relaxed">{s.body}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-charcoal group-hover:text-champagne-deep transition-colors">
                Learn More <ArrowUpRight size={14} />
              </span>
            </Link>
          ))}
        </div>

        {/* International Personal Shopping — premium highlight */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-px bg-charcoal/10 reveal">
          <div className="lg:col-span-5 bg-charcoal-deep text-ivory p-10 lg:p-14 flex flex-col justify-center order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-5">
              <Sparkles size={14} className="text-champagne" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-champagne">New Premium Service</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <Globe2 size={18} className="text-champagne" />
              <p className="text-[11px] tracking-[0.28em] uppercase text-ivory/70">International Personal Shopping</p>
            </div>
            <h3 className="font-serif text-4xl lg:text-5xl leading-[1.05]">
              Curated fashion from <em className="italic-accent">Singapore, Dubai</em> &amp; <em className="italic-accent">China.</em>
            </h3>
            <p className="mt-6 text-ivory/70 leading-relaxed max-w-md">
              Ideal for clients seeking exclusive labels, better quality, better pricing, hard-to-find products and luxury shopping assistance while travelling.
            </p>
            <Link to="/shop-the-world" className="mt-10 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-champagne hover:text-ivory transition-colors w-fit" data-testid="intl-shopping-cta">
              Explore International Shopping <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7 bg-ivory order-1 lg:order-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-charcoal/10 h-full">
              {INTL_SHOPPING_PRICING.map((p) => (
                <div key={p.id} className={`p-7 lg:p-9 flex flex-col justify-between ${p.feature ? 'bg-ivory-warm' : 'bg-ivory'}`}>
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-champagne-deep">{p.city}</p>
                    <p className="font-display text-3xl mt-3 text-charcoal">{p.price}</p>
                    <p className="text-[10px] tracking-[0.25em] uppercase mt-1 text-charcoal/50">{p.sub}</p>
                  </div>
                  <p className="mt-6 text-sm text-charcoal/70 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
